import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseImport, commitImport } from '@/services/flavor_import_api';
import type {
  ImportParseResult,
  ImportCommitMode,
  ImportSelection,
  ParseSource,
} from '@/utils/types/flavorImport';
import type { VersionDetail } from '@/utils/types/flavorVersion';
import { currentFormulationQueryKey } from '@/hooks/useCurrentFormulation';

export function useImportParse(flavorId: string, formulaId: number) {
  return useMutation<ImportParseResult, Error, ParseSource>({
    mutationFn: (source) => parseImport(flavorId, formulaId, source),
    retry: 0,
  });
}

export function useImportCommit(flavorId: string, formulaId: number) {
  const qc = useQueryClient();
  return useMutation<
    VersionDetail,
    Error,
    { parseId: string; mode: ImportCommitMode; selections: ImportSelection[] }
  >({
    mutationFn: (args) => commitImport(flavorId, formulaId, args),
    onSuccess: (newDetail) => {
      // Versions list invalidates so History reflects the new commit.
      qc.invalidateQueries({ queryKey: ['flavorVersion', flavorId, formulaId, 'list'] });
      // The mutation response IS the new VersionDetail. Seed the
      // current-formulation cache directly instead of invalidating + refetching —
      // saves a ~1.4s GET /current round-trip on every commit.
      qc.setQueryData(currentFormulationQueryKey(flavorId, formulaId), newDetail);
    },
    retry: 0,
  });
}
